import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import axios from 'axios';
import Scrollbars from 'react-custom-scrollbars';
import { AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import { axiosPath, path } from '../../../../../../../routing/helpers/router';
import * as currentPageAction from '../../../../../../../store/actions/currentPageAction';
import useIsMounted from '../../../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import AppLink from '../../../../../../../routing/helpers/AppLink';
import colors from '../../../../../../../constants/colors';
import ControlFields from './ControlFields';
import { getControlState } from '../../../tools/totalEnergiesRiskControlTools';

export default function ControlTab({ elementID }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge,
  );
  const global = useSelector((state) => state.global);
  const controls = useSelector((s) => s.currentPage?.controlTab?.[elementID]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedControl, setSelectedControl] = useState(null);

  // > LOAD CONTROL TAB
  const loadControls = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_totalEnergiesRisk_risk_control_tab'), {
          moduleBridgeHex,
          riskID: elementID,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change('controlTab', {
                [elementID]: data,
              }),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false)),
    [moduleBridgeHex, elementID],
  );

  useEffect(() => {
    if (isLoading) loadControls();
  }, [isLoading]);
  // < LOAD CONTROL TAB

  // > REDIRECT TO PLANT WITH OBLIGATION FILTER
  const createFilterAndRedirect = useCallback(
    async () =>
      axios
        .post(
          axiosPath('api_r_user_module_totalEnergiesRisk_risk_generateFilterHexCode'),
          {
            moduleBridgeHex,
            filters: { controlExpandedID: selectedControl?.id },
          },
        )
        .then(({ data }) => {
          if (data?.hexCode) {
            window.open(
              path({
                name: 'user_module_totalEnergiesRisk_control_list',
                params: {
                  moduleBridgeHex,
                  page: 1,
                  filtersHex: data?.hexCode,
                },
              }),
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, selectedControl],
  );
  // < REDIRECT TO PLANT WITH OBLIGATION FILTER

  if (isLoading) return <AppNoInfo minHeight={300} />;

  return (
    <Container>
      <SidebarMenu>
        <>
          {userBridge?.permissions?.level === 0 && (
            <AppLink
              path="user_module_totalEnergiesRisk_control_add"
              params={{
                moduleBridgeHex,
                riskID: elementID,
              }}
              className="add-button"
            >
              Nuevo control
              {` (${controls?.length || 0})`}
            </AppLink>
          )}
          {controls?.length > 0 ? (
            <Scrollbars style={{ height: '365px' }}>
              <ul>
                {controls.length > 0 &&
                  controls.map((c) => (
                    <li
                      style={{
                        borderLeftColor:
                          colors?.totalEnergiesRiskControlState[c?.state],
                        borderRightColor: c?.primary
                          ? colors.primary
                          : colors.primaryLight,
                      }}
                      title={`${
                        c?.primary ? 'Riesgo principal' : 'Riesgo secundario'
                      } - ${getControlState(c?.state)}`}
                      key={c.id}
                      className={`control ${
                        selectedControl?.id === c.id ? 'selected' : ''
                      }`}
                      onClick={() => setSelectedControl(c)}
                    >
                      <div className="first-row">{c?.name}</div>
                    </li>
                  ))}
              </ul>
            </Scrollbars>
          ) : (
            <AppNoInfo
              text="Sin controles"
              color={colors.primaryLight}
              icon={
                <AppRemixIcon
                  size={24}
                  icon="file-search"
                  color={colors.primaryLight}
                />
              }
            />
          )}
        </>
      </SidebarMenu>
      <Content>
        {selectedControl ? (
          <>
            <div className="container-fluid">
              <div className="row">
                <div className="col-lg-12 d-flex justify-content-end">
                  {userBridge?.permissions?.level === 0 && (
                    <AppLink
                      className="mr-2"
                      title="Editar"
                      path="user_module_totalEnergiesRisk_control_edit"
                      params={{
                        moduleBridgeHex,
                        controlHex: selectedControl?.hex,
                      }}
                    >
                      <AppRemixIcon
                        icon="edit"
                        className="pointer"
                        size={16}
                        onClick={() => {
                          setSelectedControl(null);
                        }}
                      />
                    </AppLink>
                  )}
                  <AppRemixIcon
                    title="Ir a Control"
                    icon="eye"
                    className="pointer"
                    size={16}
                    onClick={createFilterAndRedirect}
                  />
                  <AppRemixIcon
                    title="Cerrar"
                    icon="close"
                    className="pointer"
                    size={16}
                    onClick={() => {
                      setSelectedControl(null);
                    }}
                  />
                </div>
              </div>
            </div>
            <ControlFields values={selectedControl} isReadOnly />
          </>
        ) : (
          <AppNoInfo
            text="Sin control seleccionado"
            color={colors.primaryLight}
            icon={
              <AppRemixIcon
                size={24}
                icon="file-search"
                color={colors.primaryLight}
              />
            }
          />
        )}
      </Content>
    </Container>
  );
}

const Container = styled.div`
  height: 400px;
  display: grid;
  grid-template-columns: 260px 1fr;
`;

const SidebarMenu = styled.div`
  overflow-y: auto;
  padding: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  .add-button {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${colors.primary};
    color: ${colors.primary};
    margin: 0;
    height: 32px;
    cursor: pointer;
  }
  ul {
    padding: 4px;
    overflow-y: auto;
    li {
      &.control {
        border-left-width: 6px;
        border-left-style: solid;
        border-right-width: 6px;
        border-right-style: solid;
        .first-row {
          padding-left: 6px;
          max-height: 40px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          width: 235px;
        }
      }
      padding: 4px 6px 4px 4px;
      border-bottom: 1px solid ${colors.grey300};
      cursor: pointer;
      &.selected {
        background: ${colors.grey300};
      }
      .first-row {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        color: ${colors.primary};
        .name {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      .second-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 10px;
        .sr-date {
          letter-spacing: 1px;
        }
        .sr-user {
          color: ${colors.primary};
        }
      }
    }
  }
`;

const Content = styled.div`
  overflow-y: auto;
  border-left: 1px solid ${colors.grey300};
  padding: 4px;
  height: 400px;
  .row-no-margin > .row {
    margin: 0;
  }
  h3 {
    font-size: 13px;
    margin: 0px 0px 6px;
    font-weight: bold;
  }
`;
